import {
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
  useCallback,
  type CSSProperties,
  type FC,
  type ReactNode,
  type Ref,
} from "react";
import { createPortal } from "react-dom";
import { Loader2 } from "lucide-react";
import "@/components/ui/SearchableSelect.css";

// === File-local companions ============================================

/**
 * Option shape accepted by AsyncSearchableSelect. Polymorphic so callers may
 * pass additional domain data alongside the canonical `value`/`label` pair.
 */
interface AsyncOption {
  value: string | number;
  label: string;
  [k: string]: unknown;
}

/**
 * Imperative API exposed via `selectRef` (useImperativeHandle).
 */
interface AsyncSearchableHandle {
  focus(): void;
  open(): void;
  close(): void;
}

/**
 * Style record used for the dropdown panel. Accepts CSSProperties values
 * plus raw strings/numbers for CSS custom properties (e.g. `var(--z-…)`).
 */
type DropdownStyle = CSSProperties & Record<string, string | number | undefined>;

type SearchFn = (query: string) => Promise<void>;

interface AsyncSearchableSelectProps {
  value?: AsyncOption | null;
  onChange?: (opt: AsyncOption | null) => void;
  onSearch?: (query: string) => Promise<AsyncOption[]>;
  placeholder?: string;
  isDisabled?: boolean;
  debounceMs?: number;
  minChars?: number;
  tabIndex?: number;
  selectRef?: Ref<AsyncSearchableHandle> | null;
  renderOption?: ((option: AsyncOption) => ReactNode) | null;
  renderValue?: ((option: AsyncOption) => ReactNode) | null;
}

/**
 * AsyncSearchableSelect - Dropdown com busca server-side (lazy loading)
 *
 * Diferente do SearchableSelect que recebe options prontas, este componente
 * faz busca no servidor quando o usuário digita.
 *
 * Props:
 * - value: { value, label, ...data } | null - Valor selecionado
 * - onChange: (opt|null) => void - Callback ao selecionar
 * - onSearch: (query: string) => Promise<Array<{value, label, ...data}>> - Função de busca server-side
 * - placeholder?: string
 * - isDisabled?: boolean
 * - debounceMs?: number - Tempo de debounce em ms (default: 300)
 * - minChars?: number - Mínimo de caracteres para buscar (default: 0)
 * - tabIndex?: number
 * - selectRef?: React.Ref
 * - renderOption?: (option) => React.ReactNode - Custom render para cada item da lista
 * - renderValue?: (option) => React.ReactNode - Custom render para o valor selecionado no botão
 */
const AsyncSearchableSelect: FC<AsyncSearchableSelectProps> = ({
  value = null,
  onChange,
  onSearch,
  placeholder = "Selecione...",
  isDisabled = false,
  debounceMs = 300,
  minChars = 0,
  tabIndex,
  selectRef = null,
  renderOption = null,
  renderValue = null,
}) => {
  const [open, setOpen] = useState<boolean>(false);
  const [q, setQ] = useState<string>("");
  const [options, setOptions] = useState<AsyncOption[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [highlightedIndex, setHighlightedIndex] = useState<number>(-1);
  const [dropdownStyle, setDropdownStyle] = useState<DropdownStyle>({});
  const [hasSearched, setHasSearched] = useState<boolean>(false);

  const panelRef = useRef<HTMLDivElement | null>(null);
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Expor métodos via ref
  useImperativeHandle(selectRef, (): AsyncSearchableHandle => ({
    focus: () => {
      buttonRef.current?.focus();
      if (!isDisabled) {
        setTimeout(() => setOpen(true), 50);
      }
    },
    open: () => setOpen(true),
    close: () => setOpen(false),
  }));

  // Busca com debounce
  const debouncedSearch = useCallback<SearchFn>(async (searchQuery) => {
    if (searchQuery.length < minChars) {
      setOptions([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const results = onSearch ? await onSearch(searchQuery) : [];
      const safeResults: AsyncOption[] = results || [];
      setOptions(safeResults);
      setHasSearched(true);
      setHighlightedIndex(safeResults.length > 0 ? 0 : -1);
    } catch (error) {
      console.error('[AsyncSearchableSelect] Erro na busca:', error);
      setOptions([]);
    } finally {
      setLoading(false);
    }
  }, [onSearch, minChars]);

  // Effect para debounce da busca
  useEffect(() => {
    if (!open) return;

    // Limpar timeout anterior
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    // Se a query está vazia e minChars é 0, buscar imediatamente (lista inicial)
    if (q.trim() === "" && minChars === 0) {
      debouncedSearch("");
      return;
    }

    // Debounce para queries não vazias
    debounceRef.current = setTimeout(() => {
      debouncedSearch(q.trim());
    }, debounceMs);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [q, open, debouncedSearch, debounceMs, minChars]);

  // Carregar opções iniciais quando abre (se minChars = 0)
  useEffect(() => {
    if (open && minChars === 0 && !hasSearched && options.length === 0) {
      debouncedSearch("");
    }
  }, [open, minChars, hasSearched, options.length, debouncedSearch]);

  // Reset ao fechar
  useEffect(() => {
    if (!open) {
      setQ("");
      setOptions([]);
      setHasSearched(false);
      setHighlightedIndex(-1);
    }
  }, [open]);

  // Fecha ao clicar fora / Esc
  useEffect(() => {
    if (!open) return;
    const onKey = (e: globalThis.KeyboardEvent) => {
      if (e.key === "Escape") {
        setOpen(false);
        setDropdownStyle({});
      }
    };
    const onClick = (e: globalThis.MouseEvent) => {
      // DOM boundary: narrow EventTarget | null to Node via runtime guard.
      const target = e.target;
      if (!(target instanceof Node)) return;

      const isOnPanel = panelRef.current?.contains(target) ?? false;
      const isOnButton = buttonRef.current?.contains(target) ?? false;
      const isOnAnyPortal =
        target instanceof Element ? target.closest('.ss-panel-portal') : null;

      if (!isOnPanel && !isOnButton && !isOnAnyPortal) {
        setOpen(false);
        setDropdownStyle({});
      }
    };
    window.addEventListener("keydown", onKey);
    window.addEventListener("mousedown", onClick);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("mousedown", onClick);
    };
  }, [open]);

  const selectItem = (opt: AsyncOption) => {
    onChange?.(opt);
    setOpen(false);
    setDropdownStyle({});
  };

  // Calcular posição do dropdown
  useEffect(() => {
    if (open && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const dropdownHeight = 320;
      const openUpward = spaceBelow < dropdownHeight && rect.top > dropdownHeight;

      const newStyle: DropdownStyle = {
        position: 'fixed',
        left: `${rect.left}px`,
        width: `${Math.max(rect.width, 280)}px`,
        maxWidth: '450px',
        zIndex: 'var(--z-select-dropdown)',
        background: '#fff',
        border: '1px solid #e5e7eb',
        borderRadius: '12px',
        padding: '8px',
        boxShadow: '0 10px 40px rgba(0, 0, 0, 0.15), 0 4px 12px rgba(0, 0, 0, 0.1)',
      };

      if (openUpward) {
        newStyle.bottom = `${window.innerHeight - rect.top + 6}px`;
      } else {
        newStyle.top = `${rect.bottom + 6}px`;
      }

      setDropdownStyle(newStyle);
    }
  }, [open]);

  // Atualizar posição ao scroll/resize
  useEffect(() => {
    if (!open) return;

    const updatePosition = () => {
      if (buttonRef.current) {
        const rect = buttonRef.current.getBoundingClientRect();
        const spaceBelow = window.innerHeight - rect.bottom;
        const dropdownHeight = 320;
        const openUpward = spaceBelow < dropdownHeight && rect.top > dropdownHeight;

        const newStyle: DropdownStyle = {
          position: 'fixed',
          left: `${rect.left}px`,
          width: `${Math.max(rect.width, 280)}px`,
          maxWidth: '450px',
          zIndex: 'var(--z-select-dropdown)',
          background: '#fff',
          border: '1px solid #e5e7eb',
          borderRadius: '12px',
          padding: '8px',
          boxShadow: '0 10px 40px rgba(0, 0, 0, 0.15), 0 4px 12px rgba(0, 0, 0, 0.1)',
        };

        if (openUpward) {
          newStyle.bottom = `${window.innerHeight - rect.top + 6}px`;
        } else {
          newStyle.top = `${rect.bottom + 6}px`;
        }

        setDropdownStyle(newStyle);
      }
    };

    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);

    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [open]);

  // Conteúdo do dropdown
  const dropdownContent = open && dropdownStyle.position && (
    <div
      ref={panelRef}
      className="ss-panel ss-panel-portal"
      // CSS custom-property bridge (zIndex: 'var(--z-select-dropdown)'):
      // DropdownStyle widens CSSProperties to accept raw string; React's
      // `style` prop narrows back to CSSProperties at the assignment site.
      style={dropdownStyle as CSSProperties}
      onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
    >
      <input
        ref={inputRef}
        className="ss-search"
        autoFocus
        placeholder="Digite para buscar..."
        value={q}
        onChange={(e) => {
          setQ(e.target.value);
          setHighlightedIndex(0);
        }}
        onKeyDown={(e) => {
          const totalItems = options.length;

          if (e.key === 'ArrowDown') {
            e.preventDefault();
            setHighlightedIndex(prev => Math.min(prev + 1, totalItems - 1));
          } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setHighlightedIndex(prev => Math.max(prev - 1, 0));
          } else if (e.key === 'Enter') {
            e.preventDefault();
            if (highlightedIndex >= 0 && options[highlightedIndex]) {
              selectItem(options[highlightedIndex]);
            }
          } else if (e.key === 'Tab') {
            if (options.length === 1) {
              e.preventDefault();
              selectItem(options[0]);
            } else if (highlightedIndex >= 0 && options[highlightedIndex]) {
              e.preventDefault();
              selectItem(options[highlightedIndex]);
            }
          }
        }}
      />

      <div className="ss-list" role="listbox">
        {/* Loading */}
        {loading && (
          <div className="ss-empty" style={{ display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'center' }}>
            <Loader2 size={16} className="animate-spin" style={{ animation: 'spin 1s linear infinite' }} />
            Buscando...
          </div>
        )}

        {/* Nada encontrado */}
        {!loading && hasSearched && options.length === 0 && (
          <div className="ss-empty">Nenhum resultado encontrado</div>
        )}

        {/* Instrução inicial */}
        {!loading && !hasSearched && minChars > 0 && (
          <div className="ss-empty" style={{ color: '#6b7280', fontSize: '12px' }}>
            Digite pelo menos {minChars} caracteres para buscar
          </div>
        )}

        {/* Lista de resultados */}
        {!loading && options.length > 0 && (
          options.slice(0, 50).map((o, index) => {
            const selected = value && String(value.value) === String(o.value);
            const isHighlighted = index === highlightedIndex;
            return (
              <div
                key={String(o.value)}
                role="option"
                aria-selected={selected ?? false}
                className={`ss-item${isHighlighted ? ' ss-item-highlighted' : ''}`}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  background: isHighlighted ? '#dbeafe' : undefined,
                  padding: renderOption ? '6px 12px' : undefined,
                }}
                onClick={() => selectItem(o)}
                onMouseEnter={() => setHighlightedIndex(index)}
              >
                {renderOption ? (
                  renderOption(o)
                ) : (
                  <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {o.label}
                  </span>
                )}
              </div>
            );
          })
        )}

        {/* Aviso de mais resultados */}
        {!loading && options.length > 50 && (
          <div className="ss-empty" style={{ color: '#6b7280', fontSize: '11px', padding: '4px 12px' }}>
            Mostrando 50 de {options.length}. Refine sua busca.
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className="ss">
      <button
        ref={buttonRef}
        type="button"
        className="ss-toggle"
        onClick={() => setOpen((s) => !s)}
        onKeyDown={(e) => {
          if (!open && (e.key === 'Enter' || e.key === ' ' || e.key.length === 1)) {
            e.preventDefault();
            setOpen(true);
            if (e.key.length === 1 && e.key !== ' ') {
              setQ(e.key);
              setHighlightedIndex(0);
            }
          }
        }}
        disabled={isDisabled}
        tabIndex={tabIndex}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        {value ? (
          renderValue ? renderValue(value) : <span>{value.label}</span>
        ) : (
          <span className="ss-placeholder">{placeholder}</span>
        )}
      </button>

      {dropdownContent && createPortal(dropdownContent, document.body)}
    </div>
  );
};

export default AsyncSearchableSelect;
